/**
 * Web clipper inbox (ADR 0022): the browser extension puts clips in the `clips`
 * table; the first open app takes each one (delete … returning, so no other
 * device gets it too) and turns it into a page under "Webclips".
 */
import { useEffect } from 'react';
import { takeClips } from '@nexus/sync';
import { clipToPage } from '@nexus/workspace';
import { useStore } from '../db/context';
import { server } from './config';
import { supabase, useSyncState } from './useSync';

const INTERVAL_MS = 120_000;

export function useClipInbox() {
  const store = useStore();
  const userId = useSyncState((s) => s.session?.user.id ?? null);
  // Only after the first sync: the "Webclips" folder may already exist on another device.
  const synced = useSyncState((s) => !!s.status.lastSyncAt);

  useEffect(() => {
    if (!userId || !synced) return;
    const sb = supabase();
    let busy = false;
    let again = false;

    const take = async () => {
      if (busy) return void (again = true);
      busy = true;
      try {
        do {
          again = false;
          await takeClips(sb, (clip) =>
            clipToPage(store, {
              title: clip.title,
              url: clip.url,
              markdown: clip.markdown,
              note: clip.note,
              clippedAt: Date.parse(clip.created_at),
            }),
          );
        } while (again);
      } catch (e) {
        console.error('clips', e);
      } finally {
        busy = false;
      }
    };

    void take();
    const channel = sb
      .channel('nexus-clips')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: server().schema,
          table: 'clips',
          filter: `owner_id=eq.${userId}`,
        },
        () => void take(),
      )
      .subscribe();
    const interval = setInterval(() => void take(), INTERVAL_MS);
    return () => {
      clearInterval(interval);
      void sb.removeChannel(channel);
    };
  }, [store, userId, synced]);
}

/** The first link in a shared text (Android often puts the URL there). */
export function sharedClip(params: URLSearchParams) {
  const text = params.get('text')?.trim() ?? '';
  const url = params.get('url')?.trim() || text.match(/https?:\/\/\S+/)?.[0] || null;
  const rest = url ? text.replace(url, '').trim() : text;
  const title = params.get('title')?.trim() || rest.split('\n')[0]?.slice(0, 120) || url || '';
  return { title, url, note: rest && rest !== title ? rest : null };
}

/** Wait (at most 15 s) until a signed-in device has synced once, so special folders are known. */
function afterFirstSync(): Promise<void> {
  const ready = () => {
    const s = useSyncState.getState();
    return s.authReady && (!s.session || !!s.status.lastSyncAt);
  };
  if (ready()) return Promise.resolve();
  return new Promise((resolve) => {
    const done = () => {
      clearTimeout(timer);
      off();
      resolve();
    };
    const timer = setTimeout(done, 15_000);
    const off = useSyncState.subscribe(() => ready() && done());
  });
}

/**
 * Android share sheet → Nexus (PWA `share_target`): `/share?title=…&text=…&url=…`
 * becomes a page under "Webclips", which then opens.
 */
export function useShareTarget(onPage: (pageId: string) => void) {
  const store = useStore();
  useEffect(() => {
    if (window.location.pathname !== '/share') return;
    const params = new URLSearchParams(window.location.search);
    window.history.replaceState(null, '', '/');
    const clip = sharedClip(params);
    if (!clip.title && !clip.url) return;
    void afterFirstSync()
      .then(() => clipToPage(store, clip))
      .then((page) => onPage(page.id))
      .catch((e: unknown) => console.error('share', e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store]);
}
